<script setup lang="ts">
import type { PerActionFieldRulesOrWildcard, PerActionItemRule, PolicyDocumentAction } from '#layers/access/types/permissions/policy';
import type { BlockRootContext } from '#layers/block-engine/public';
import type { FormBlockSuccessEventPayload } from '#layers/blocks/public';
import type { PolicyDrawerProps } from './PolicyCollectionDrawer.vue';
import { cloneDeep } from 'lodash-es';
import { computed, ref } from 'vue';
import { isFieldRulesAction, isItemRulesAction } from '#layers/access/utils/policies';
import { useWorkspaceSchemaStore } from '#layers/data-model/public';
import { provideRelatedItemDrawer } from '#layers/filters/public';
import { useCollectionItemPicker } from '#layers/listing/public';
import { PageShell } from '#layers/ui-workspace/public';
import { DrawerPage, PageSlotProvider, TeleportPageActions } from '#layers/ui/public';
import PolicyFieldRules from './PolicyFieldRules.vue';
import PolicyItemRules from './PolicyItemRules.vue';

interface PolicyCustomSettingsDrawerProps extends PolicyDrawerProps {
	action: PolicyDocumentAction;
}

const props = defineProps<PolicyCustomSettingsDrawerProps>();
const emit = defineEmits<{
	close: [payload?: FormBlockSuccessEventPayload];
}>();

provideRelatedItemDrawer(useCollectionItemPicker());

const { findCollectionById } = useWorkspaceSchemaStore();
const collection = computed(() => findCollectionById(props.collectionId));
const collectionName = computed<string>(() => collection.value?.apiName ?? '');

const blockContext = computed<BlockRootContext>(() => ({
	itemId: props.itemId,
	formMode: props.formMode,
	itemEdits: props.itemEdits,
	pageType: 'detail',
	pageIsFullWidth: false,
	pageDisplayMode: 'drawer',
}));

const itemRules = ref<PerActionItemRule>(cloneDeep(props.content.itemRules?.[collectionName.value]?.[props.action] ?? {}));
const fieldRules = ref<PerActionFieldRulesOrWildcard>(cloneDeep(props.content.fieldRules?.[collectionName.value]?.[props.action] ?? {}));

function save() {
	emit('close', {
		key: props.itemId!,
		data: {
			fieldRules: fieldRules.value,
			itemRules: itemRules.value,
		},
	});
}
</script>

<template>
	<DrawerPage
		:attach-to-route="false"
		:ui="{ container: 'gap-0 2xl:w-200', bodyContent: 'p-0' }"
		@close="() => emit('close')"
	>
		<template #header>
			<slot name="header">
				<h2 class="flex-1 text-lg font-semibold">
					<span class="flex-1 font-mono text-lg font-medium">{{ collectionName }}</span> → {{ $t(`policy.action.${action}`).toLowerCase() }}
				</h2>
			</slot>
		</template>
		<PageSlotProvider>
			<PageShell :context="blockContext">
				<div class="flex flex-col gap-8">
					<PolicyItemRules
						v-if="isItemRulesAction(action)"
						v-model="itemRules"
						:collection-id
						:action
					/>
					<PolicyFieldRules
						v-if="isFieldRulesAction(action)"
						v-model="fieldRules"
						:item-rules
						:collection-id
						:action
					/>
				</div>
			</PageShell>
			<TeleportPageActions>
				<DButton color="neutral" variant="outline" :label="$t('common.cancel')" @click="emit('close')" />
				<DButton :label="$t('common.save')" @click="save" />
			</TeleportPageActions>
		</PageSlotProvider>
	</DrawerPage>
</template>
